import isEqual from 'lodash/isEqual';
import intersectionBy from 'lodash/intersectionBy';
import unionBy from 'lodash/unionBy';
import pick from 'lodash/pick';

import {ResourceType as PropertyFilterResourceType} from 'common/report/constants';
import {customPropertiesStore, CustomPropertyResourceType} from 'common/report/stores/custom-properties';
import {baseComparator, capitalize} from 'common/util';
import {isPersistedCustomProperty} from 'common/widgets/custom-property-modal/util';
import {Segmentation} from 'common/widgets/property-filter-menu/models/segfilter';
import {PropertyDefinition} from 'common/util/definitions-types';
import {SectionItem} from 'common/components/items-menu/types';

import {PropertyItem} from './types';

/**
 * Find intersection of Mixpanel properties present in all given property lists.
 * Checks equality via `name` prop.
 */
export function calculatePropertiesIntersection(propertyLists: PropertyItem[]) {
  // @ts-ignore - TODO: Argument of type 'PropertyItem' is not assignable to parameter of type 'ArrayLike<string>'.
  return intersectionBy(...propertyLists, `name`);
}

/**
 * Find union of Mixpanel properties present in all given property lists.
 * Checks equality via `name` prop.
 */
export function calculatePropertiesUnion(propertyLists: PropertyItem[]) {
  // @ts-ignore - TODO: Expected at least 1 arguments, but got 1 or more.
  return unionBy(...propertyLists, `name`);
}

export function sanitizeCustomProperty(customProperty) {
  return pick(customProperty, [
    `behavior`,
    `composedProperties`,
    `mappedDataGroupId`,
    `referencedDirectlyBy`,
    `referencedBy`,
    `referencedRawEventProperties`,
    `description`,
    `displayFormula`,
    `name`,
    `resourceType`,
    `propertyType`,
    `joinPropertyType`,
  ]);
}

export const CUSTOM_PROPERTY_RESOURCE_TYPE_MAPPING = {
  [CustomPropertyResourceType.Events]: PropertyFilterResourceType.Event,
  [CustomPropertyResourceType.People]: PropertyFilterResourceType.User,
};

export function storeCustomPropertyToProperty(customPropertyId): PropertyItem {
  let customProperty = customPropertiesStore.value[customPropertyId];
  customProperty = sanitizeCustomProperty(customProperty);
  const {name, propertyType, mappedDataGroupId, joinPropertyType} = customProperty;
  const resourceType = CUSTOM_PROPERTY_RESOURCE_TYPE_MAPPING[customProperty.resourceType] || null;

  if (isPersistedCustomProperty(customPropertyId)) {
    customPropertyId = Number(customPropertyId);
  }

  return {
    customProperty,
    customPropertyId,
    dimensionDataGroupId: mappedDataGroupId,
    resourceType,
    propertyType,
    propertyDefaultType: propertyType,
    type: propertyType,
    joinPropertyType,
    value: name,
    label: name,
  };
}

/**
 * Compare if two properties are the same, ignoring typecast, changes in custom property definitions, etc...
 * NOTE: Even for inline custom properties, they must have the same temporary id to be considered the same.
 */
export function isSamePropertyReference(property1, property2): boolean {
  if (property1.customPropertyId || property2.customPropertyId) {
    return property1.customPropertyId === property2.customPropertyId;
  }
  const PROPERTY_KEY_FIELDS = [`resourceType`, `propertyName`];
  return isEqual(pick(property1, PROPERTY_KEY_FIELDS), pick(property2, PROPERTY_KEY_FIELDS));
}

/**
 * Generates a Segmentation format for user or event PropertyItem types
 * used in property menu. In general, Segmentation is what we use for bookmarks.
 *
 * TODO: Strongly type the input, theres a bug in the type of propertyItem
 * not transfering custom properties correctly.
 */
export function propertyItemToSegmentation(propertyItem, resourceType): Segmentation {
  const {
    customProperty,
    customPropertyId,
    dimensionDataGroupId,
    dataGroupId,
    tempDataGroupId,
    joinPropertyType,
    propertyDefaultType,
    type,
    unit,
    value,
  } = propertyItem;

  return {
    ...(customPropertyId ? {customPropertyId} : null),
    ...(customProperty ? {customProperty} : null),
    ...(dataGroupId ? {dataGroupId} : dimensionDataGroupId && Array.isArray(value) ? {dimensionDataGroupId} : null),
    ...(tempDataGroupId ? {tempDataGroupId} : {}),
    ...(joinPropertyType ? {joinPropertyType} : null),
    resourceType,
    propertyDefaultType,
    propertyName: value,
    propertyType: type,
    ...(unit ? {unit} : {}),
  };
}

/**
 * Generates a PropertyItem format for the properties menu from
 * the Segmentation format used in bookmarks usually.
 *
 * TODO: Strongly type the result, theres a bug in the type of propertyItem
 * not typing custom properties correctly.
 */
export function segmentationToPropertyItem(segment: Segmentation) {
  const propertyType = segment.propertyType || (segment.customProperty && segment.customProperty.propertyType);
  return {
    ...(segment.customPropertyId ? {customPropertyId: segment.customPropertyId} : null),
    ...(segment.customProperty ? {customProperty: segment.customProperty} : null),
    dataGroupId: segment.dataGroupId,
    ...(segment.tempDataGroupId ? {tempDataGroupId: segment.tempDataGroupId} : null),
    ...(segment.joinPropertyType ? {joinPropertyType: segment.joinPropertyType} : null),
    propertyDefaultType: segment.propertyDefaultType || propertyType,
    resourceType: segment.resourceType,
    type: propertyType,
    value: segment.propertyName,
  };
}

export function sortPropertyItems(items: PropertyItem[]) {
  return items.sort(baseComparator({transform: (item: PropertyItem) => item.label.toLowerCase()}));
}

export function getDataDefinitionsPropertyMenuItem(propertyDefinition: PropertyDefinition) {
  return {
    ...propertyDefinition,
    label: propertyDefinition.displayName?.length > 0 ? propertyDefinition.displayName : propertyDefinition.name,
    value: propertyDefinition.name,
    searchableContent: [propertyDefinition?.description, propertyDefinition?.name].join(` `),
  };
}

export function createHiddenMatchItem(
  resourceType: string,
  matchCount: number,
  {includeResourceInLabel} = {includeResourceInLabel: false},
): SectionItem {
  let item = null;
  if (matchCount > 0) {
    const matchAdjective = includeResourceInLabel ? `Hidden ${capitalize(resourceType)}` : `Hidden`;
    item = {
      label: `Show ${matchAdjective} Matches (${matchCount})`,
      value: {
        showHiddenResource: resourceType,
      },
      icon: `caret-down`,
    };
  }
  return item;
}
